import { pluralCount } from "@appretio/shared";
import { CopyCellSkipReason, CopyPlan, CopyRowReason } from "./copyPlan";
import type { CopyTarget } from "./copyTarget";

/**
 * Cells skipped inside a row the copy writes, by cause. A blank source cell is
 * not one (`specs/domain/WIP/forecast-algorithms/SPEC.md` § Partial copies are
 * normal).
 */
const CELL_REASON: Record<
  Exclude<CopyCellSkipReason, "noSourceValue">,
  string
> = { notEditable: "outside the editable window" };

/**
 * Rows the copy writes nothing of, by cause, in the order the toast lists
 * them. Own rows first: they are the target, and the menu label's "2 of 4"
 * has already told the planner they drop out.
 */
function rowReasons(
  target: CopyTarget
): Record<CopyRowReason, (count: number) => string> {
  return {
    ownRow: (count) => pluralCount(count, target.ownRow),
    metricNotEditable: (count) => pluralCount(count, "read-only KPI"),
    noTargetRow: (count) =>
      `${pluralCount(count, "row")} ${target.notInTarget}`,
    notEditable: (count) =>
      `${pluralCount(count, "row")} outside the editable window`,
    noSourceValue: (count) => `${pluralCount(count, "row")} with no values`
  };
}

/** The reasons with a count, in `labels` order. */
function counted<Reason extends string>(
  tally: Partial<Record<Reason, number>>,
  labels: Record<Reason, unknown>
): [Reason, number][] {
  return (Object.keys(labels) as Reason[])
    .map((reason) => [reason, tally[reason] ?? 0] as [Reason, number])
    .filter(([, count]) => count > 0);
}

/**
 * The toast text for a copy: what it writes, then — only when there is
 * something to say — the cells it skipped inside the rows it wrote, and the
 * rows it wrote nothing of. The verb follows the write — `queued` while it is
 * in flight, `landed` once the grid holds it. The menu never offers an empty
 * plan.
 */
export function describeCopyResult(
  { changes, skipped, rowsNotCopied }: CopyPlan,
  outcome: "queued" | "landed",
  target: CopyTarget
): string {
  const verb = outcome === "landed" ? "Copied" : "Copying";
  const sentences = [`${verb} ${pluralCount(changes.length, "value")}.`];

  for (const [reason, count] of counted(skipped, CELL_REASON)) {
    sentences.push(
      `${pluralCount(count, "cell")} skipped (${CELL_REASON[reason]}).`
    );
  }

  const rowReason = rowReasons(target);
  const rows = counted(rowsNotCopied, rowReason);
  if (rows.length > 0) {
    const detail = rows
      .map(([reason, count]) => rowReason[reason](count))
      .join(", ");
    sentences.push(`Not copied: ${detail}.`);
  }

  return sentences.join(" ");
}
